import React, { useState, useRef } from 'react';
import { X, Mail, Image as ImageIcon, Send, AlertCircle, CheckCircle } from 'lucide-react';
import { sendSupportRequest, uploadImageBase64 } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import './SupportModal.css';

const SupportModal = ({ isOpen, onClose, isAuthenticated = true }) => {
  const { language } = useLanguage();
  const t = translations[language];
  const fileInputRef = useRef(null);
  
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [status, setStatus] = useState(null); // 'success' | 'error' | null

  const subjectOptions = [
    { value: 'bug_report', label: t.support?.bugReport || '🐛 Bug Report' },
    { value: 'feature_request', label: t.support?.featureRequest || '💡 Feature Request' },
    { value: 'account_issue', label: t.support?.accountIssue || '👤 Account Issue' },
    { value: 'payment_billing', label: t.support?.paymentBilling || '💳 Payment/Billing' },
    { value: 'technical_support', label: t.support?.technicalSupport || '🔧 Technical Support' },
    { value: 'other', label: t.support?.other || '📝 Other' },
  ];

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setStatus({ type: 'error', message: 'Please select an image file' });
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setStatus({ type: 'error', message: 'Image must be less than 5MB' });
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload to R2 immediately
    await uploadImage(file);
  };

  const uploadImage = async (file) => {
    setUploadingImage(true);
    setStatus(null);

    try {
      console.log('🔄 Starting image upload...');
      console.log('  File:', file.name, file.type, file.size, 'bytes');
      
      // Upload to R2 - pass file directly like ProblemSolver does
      console.log('  Uploading to R2...');
      const publicUrl = await uploadImageBase64(file);
      
      console.log('✅ Upload successful!');
      console.log('  Public URL:', publicUrl);
      
      setImageUrl(publicUrl);
      setStatus({ type: 'success', message: 'Image uploaded successfully' });
      
    } catch (error) {
      console.error('❌ Upload failed:', error);
      console.error('  Error details:', error.message);
      setStatus({ type: 'error', message: 'Failed to upload image' });
      setImagePreview(null);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setImageUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!subject || !message.trim()) {
      setStatus({ type: 'error', message: 'Please fill in all required fields' });
      return;
    }

    // Validate guest fields if not authenticated
    if (!isAuthenticated) {
      if (!name.trim()) {
        setStatus({ type: 'error', message: 'Please enter your name' });
        return;
      }
      if (!email.trim()) {
        setStatus({ type: 'error', message: 'Please enter your email' });
        return;
      }
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setStatus({ type: 'error', message: 'Please enter a valid email address' });
        return;
      }
    }

    if (message.trim().length < 10) {
      setStatus({ type: 'error', message: 'Message must be at least 10 characters' });
      return;
    }

    setLoading(true);
    setStatus(null);

    console.log('📤 Submitting support request:');
    console.log('  Subject:', subject);
    console.log('  Message length:', message.length);
    console.log('  Has image URL:', !!imageUrl);
    console.log('  Image URL:', imageUrl);
    console.log('  Is Authenticated:', isAuthenticated);

    try {
      const requestData = {
        subject,
        message: message.trim(),
        image_url: imageUrl
      };

      // Add guest info if not authenticated
      if (!isAuthenticated) {
        requestData.guest_name = name.trim();
        requestData.guest_email = email.trim();
      }
      
      console.log('  Request data:', requestData);
      
      await sendSupportRequest(requestData);

      setStatus({ 
        type: 'success', 
        message: 'Support request sent! We\'ll get back to you soon.' 
      });

      // Reset form after 2 seconds
      setTimeout(() => {
        handleClose();
      }, 2000);

    } catch (error) {
      console.error('❌ Failed to send support request:', error);
      setStatus({ 
        type: 'error', 
        message: error.message || 'Failed to send support request. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSubject('');
    setMessage('');
    setName('');
    setEmail('');
    setImagePreview(null);
    setImageUrl(null);
    setStatus(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="support-modal-overlay" onClick={handleClose} />
      
      <div className="support-modal">
        {/* Header */}
        <div className="support-modal-header">
          <div className="support-modal-title-section">
            <Mail className="support-modal-icon" size={24} />
            <h2 className="support-modal-title">
              {t.support?.title || 'Contact Support'}
            </h2>
          </div>
          <button onClick={handleClose} className="support-modal-close">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="support-modal-body">
          {/* Guest Name (only if not authenticated) */}
          {!isAuthenticated && (
            <div className="support-form-group">
              <label className="support-label">
                {t.support?.name || 'Your Name'} <span className="required">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="support-input"
                placeholder={t.support?.namePlaceholder || 'Enter your name'}
                required
                disabled={loading}
              />
            </div>
          )}

          {/* Guest Email (only if not authenticated) */}
          {!isAuthenticated && (
            <div className="support-form-group">
              <label className="support-label">
                {t.support?.email || 'Your Email'} <span className="required">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="support-input"
                placeholder={t.support?.emailPlaceholder || 'Enter your email'}
                required
                disabled={loading}
              />
            </div>
          )}

          {/* Subject Dropdown */}
          <div className="support-form-group">
            <label className="support-label">
              {t.support?.subject || 'Subject'} <span className="required">*</span>
            </label>
            <select
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="support-select"
              required
              disabled={loading}
            >
              <option value="">{t.support?.selectSubject || 'Select a subject...'}</option>
              {subjectOptions.map(option => (
                <option key={option.value} value={option.label}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Message Textarea */}
          <div className="support-form-group">
            <label className="support-label">
              {t.support?.message || 'Message'} <span className="required">*</span>
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="support-textarea"
              placeholder={t.support?.messagePlaceholder || 'Describe your issue or question in detail...'}
              rows={6}
              required
              disabled={loading}
              minLength={10}
            />
            <div className="support-char-count">
              {message.length} / 10 minimum
            </div>
          </div>

          {/* Image Upload */}
          <div className="support-form-group">
            <label className="support-label">
              {t.support?.attachment || 'Attachment (Optional)'}
            </label>
            
            {!imagePreview ? (
              <div className="support-upload-area">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="support-file-input"
                  disabled={loading || uploadingImage}
                />
                <div className="support-upload-content">
                  <ImageIcon size={32} />
                  <p className="support-upload-text">
                    {uploadingImage 
                      ? 'Uploading...' 
                      : t.support?.uploadPrompt || 'Click to upload an image (5MB max)'}
                  </p>
                  <p className="support-upload-hint">
                    {t.support?.uploadHint || 'PNG, JPG, GIF up to 5MB'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="support-image-preview">
                <img src={imagePreview} alt="Preview" />
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="support-remove-image"
                  disabled={loading}
                >
                  <X size={16} />
                </button>
              </div>
            )}
          </div>

          {/* Status Message */}
          {status && (
            <div className={`support-status ${status.type}`}>
              {status.type === 'success' ? (
                <CheckCircle size={18} />
              ) : (
                <AlertCircle size={18} />
              )}
              <span>{status.message}</span>
            </div>
          )}

          {/* Footer */}
          <div className="support-modal-footer">
            <button
              type="button"
              onClick={handleClose}
              className="support-btn support-btn-cancel"
              disabled={loading}
            >
              {t.cancel || 'Cancel'}
            </button>
            <button
              type="submit"
              className="support-btn support-btn-submit"
              disabled={loading || uploadingImage || !subject || !message.trim()}
            >
              {loading ? (
                <>
                  <span className="support-spinner" />
                  {t.support?.sending || 'Sending...'}
                </>
              ) : (
                <>
                  <Send size={18} />
                  {t.support?.send || 'Send Message'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default SupportModal;